/**
 * The derived-relations panel — docs/08-frontend-and-ux.md §3.4, over
 * `GET /persons/:id/derived-relations` (docs/05 §6.6).
 *
 * Two things this panel has to teach, and the styling carries both:
 *
 *  - **These rows are computed, not recorded.** They are lighter than the stored
 *    assertions above them and carry a "computed" chip, because a member who
 *    thinks "first cousin" is a stored fact will look for a way to edit it. There
 *    isn't one, by design (ADR-0003) — you change the parents, and the cousinhood
 *    follows.
 *  - **The path is available.** Every row names the people it was derived
 *    through, so an answer that looks wrong can be traced to the assertion that
 *    made it wrong.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DERIVED_KIND, type DerivedKind, type DerivedRelation } from '@famtree/shared';
import { kinshipApi } from '../../api/client.js';
import { personName } from '../people/labels.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './kinship.module.css';

/** How many rows a group shows before it asks to be expanded. */
const COLLAPSED_ROWS = 6;

export function DerivedRelationsPanel({ personId }: { personId: string }): JSX.Element {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['derived-relations', personId],
    queryFn: () => kinshipApi.derivedRelations(personId),
    enabled: personId !== '',
  });

  if (query.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  // docs/08 §13: the profile loads even when this panel fails — it retries on
  // its own rather than taking the page down with it.
  if (query.isError) {
    return (
      <div className={styles.derivedCard}>
        <p className={ui.problem}>{problemMessage(query.error, t)}</p>
        <button type="button" className={ui.buttonSecondary} onClick={() => void query.refetch()}>
          {t('app.retry')}
        </button>
      </div>
    );
  }

  const { data, truncated } = query.data;
  if (data.length === 0) {
    return (
      <section className={styles.derivedCard}>
        <h2 className={styles.groupTitle}>{t('kinship.panelTitle')}</h2>
        <p className={ui.muted}>{t('kinship.panelEmpty')}</p>
      </section>
    );
  }

  // Grouped in the enum's own order, which runs closest-first: parents and
  // children before cousins and in-laws.
  const grouped = new Map<DerivedKind, DerivedRelation[]>();
  for (const row of data) {
    grouped.set(row.kind, [...(grouped.get(row.kind) ?? []), row]);
  }
  const groups = DERIVED_KIND.filter((kind) => grouped.has(kind));

  return (
    <section className={styles.derivedCard} aria-labelledby="derived-relations-title">
      <h2 id="derived-relations-title" className={styles.groupTitle}>
        {t('kinship.panelTitle')} <span className={styles.chip}>{t('kinship.computed')}</span>
      </h2>
      <p className={ui.muted}>{t('kinship.panelNote')}</p>

      {groups.map((kind) => (
        <DerivedGroup key={kind} kind={kind} rows={grouped.get(kind)!} />
      ))}

      {truncated && <p className={ui.muted}>{t('kinship.panelTruncated')}</p>}
    </section>
  );
}

function DerivedGroup({ kind, rows }: { kind: DerivedKind; rows: DerivedRelation[] }): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);

  return (
    <div>
      <h3 className={styles.groupTitle}>{t(`kinship.groups.${kind}`)}</h3>
      <ul className={styles.list}>
        {shown.map((row) => (
          <li key={`${row.person.id}-${row.label}`}>
            <Link className={styles.row} to={`/people/${row.person.id}`}>
              <span className={styles.rowName}>{personName(row.person)}</span>
              <span className={styles.label}>{row.label}</span>
              <span className={styles.spacer} />
              {row.annotations
                .filter((annotation) => annotation !== 'unknown_basis')
                .map((annotation) => (
                  <span key={annotation} className={styles.chip}>
                    {t(`kinship.annotations.${annotation}`)}
                  </span>
                ))}
              {row.person.status === 'pending' && (
                <span className={styles.chip}>{t('enums.assertion_status.pending')}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
      {rows.length > COLLAPSED_ROWS && (
        <button
          type="button"
          className={ui.buttonSecondary}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? t('kinship.showFewer') : `${t('kinship.showAll')} (${String(rows.length)})`}
        </button>
      )}
    </div>
  );
}
